import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import i18n from "@/i18n";
import { RootState } from "@/store/store";
import { Floor, QrCode } from "@/types/globalTypes";
import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { FaRegTrashAlt } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { useSelector } from "react-redux";

type ManageTablesButtonProps = {
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>
}

export default function ManageTablesButton({ setNeedRefetch }: ManageTablesButtonProps) {
  const { t } = useTranslation();
  const lang = i18n.language;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<Floor>();
  const [floors, setFloors] = useState<Floor[]>()
  const token = useSelector((state: { auth: { token: string } }) => state.auth.token);
  const branch = useSelector((state: RootState) => state.branch.branch);
  const baseUrl = import.meta.env.VITE_BASE_URL
  const [tableNumber, setTableNumber] = useState("")
  const [chairsCount, setchairsCount] = useState("")
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [QR, setQR] = useState<QrCode[]>();
  const [selectedQR, setSelectedQR] = useState("");
  const biggestFloor = getBiggestFloor();
  const [loading, setLoading] = useState(false);
  useLockBodyScroll(isModalOpen);
  function toggleConfirmDelete() {
    setConfirmDelete(!confirmDelete);
  }
  function getBiggestFloor() {
    if (!floors || floors.length === 0) return null;

    return floors.reduce((max, floor) =>
      parseInt(String(floor.number)) > parseInt(String(max.number)) ? floor : max
    );
  };
  async function getQr() {

    try {
      const options = {
        url: `${baseUrl}/api/dashboard/qr-codes?filter=unlinked&locale=${lang}`,
        method: "GET",
        headers: {
          Authorization: token,
        },

      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        setQR(response.data.qrcodes)
      }
    } catch (error) {
      console.error(error);
      toast.error(t("manageTables.error") as string)
    }
  }
  async function getFloors() {

    try {
      const options = {
        url: `${baseUrl}/api/dashboard/floors?branch=${branch?._id}&locale=${lang}`,
        method: "GET",
        headers: {
          Authorization: token,
        },

      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        setFloors(response.data.floors)
        setActiveTab(response.data.floors[0])
      }
    } catch (error) {
      console.error(error);
      toast.error(t("manageTables.error") as string)
    }
  }
  async function deleteFloor() {
    const toastId = toast.loading(t("manageTables.deletingFloor") as string)
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/floors/${activeTab?._id}`,
        method: "DELETE",
        headers: {
          Authorization: token,
        },

      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        toast.success(t("manageTables.floorDeleted") as string)
        getFloors()
        toggleConfirmDelete()
        setNeedRefetch(true)

      }
    } catch (error) {
      console.error(error);
      toast.error(t("manageTables.error") as string)
    } finally {
      toast.dismiss(toastId)
    }
  }
  async function addFloor() {

    const toastId = toast.loading(t("manageTables.addingTable") as string)
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/floors`,
        method: "POST",
        headers: {
          Authorization: token,
        },
        data: {
          number: parseInt(String(biggestFloor?.number)) + 1,
          branch: branch?._id
        }
      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        toast.success(t("manageTables.floorAdded") as string)
        getFloors()
        setNeedRefetch(true)

      }
    } catch (error) {
      console.error(error);
      toast.error(t("manageTables.error") as string)
    } finally {
      toast.dismiss(toastId)
    }
  }
  async function addTable(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    const toastId = toast.loading(t("manageTables.addingTable") as string)
    try {
      const options = {
        url: `${baseUrl}/api/dashboard/floors/${activeTab?._id}/tables`,
        method: "POST",
        headers: {
          Authorization: token,
        },
        data: {
          number: tableNumber,
          chairsCount: chairsCount,
          qrcode: selectedQR === "" ? undefined : selectedQR

        }
      };
      const response = await axios.request(options);
      if (response.data.success == true) {
        toast.success(t("manageTables.floorAdded") as string)
        setNeedRefetch(true)
        setTableNumber("")
        setchairsCount("")
        setSelectedQR("")
      }
    } catch (error) {
      console.error(error);
      toast.error(t("manageTables.error") as string)
    } finally {
      toast.dismiss(toastId)
      setLoading(false)
    }
  }
  useEffect(() => {
    getFloors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch])
  useEffect(() => {
    getQr()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  return (
    <>
      {confirmDelete && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50">
          <div className="flex w-[95%] max-w-3xl flex-col gap-3 rounded-lg p-5 bg-white shadow-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("manageTables.deleteFloor")} {activeTab?.number}</h2>
              <button
                type="button"
                onClick={toggleConfirmDelete}
                className="text-gray-600 hover:text-gray-800"
              >
                <IoMdClose size={24} className="text-red-600" />
              </button>
            </div>
            <p className="text-gray-600">
              {t("manageTables.confirmDelete")}{t("manageTables.floor")} {activeTab?.number}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={toggleConfirmDelete}
                className="flex items-center justify-center gap-2 rounded-md bg-sidebar text-skyblue   px-3 py-1 text-lg font-semibold  md:gap-3"
              >
                <span>{t("manageTables.cancel")}</span>
              </button>
              <button
                type="button"
                onClick={deleteFloor}
                className="flex items-center justify-center gap-2 rounded-md   bg-red-600 px-3 py-1 text-lg font-semibold text-white md:gap-3"
              >
                <span>{t("manageTables.delete")}</span>
              </button>
            </div>
          </div>
        </div>
      )}
      <div >
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center gap-2 rounded-md border border-border bg-sidebar px-3 py-1 text-lg font-semibold text-skyblue md:gap-3"
        >
          <span>{t("manageTables.title")}</span>
        </button>

        {isModalOpen && (
          <div
            className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-[70px]"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex w-[95%] max-w-3xl flex-col gap-3 rounded-lg p-5 bg-white shadow-lg">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{t("manageTables.title")}</h2>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  <IoMdClose size={24} className="text-red-600" />
                </button>
              </div>

              {/* Tabs with Horizontal Scroll */}
              <div className="flex overflow-x-auto space-x-2  mb-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                {floors?.map((floor) => (
                  <button
                    type="button"
                    key={floor._id}
                    className={`whitespace-nowrap px-4 py-2 flex items-center gap-2 text-center ${activeTab?._id === floor._id ? "border-b-2 border-sidebar text-sidebar" : "text-gray-600"
                      }`}
                    onClick={() => setActiveTab(floor)}
                  >
                    {t("manageTables.floor")} {floor.number} {floor.number !== "1" && <FaRegTrashAlt onClick={() => toggleConfirmDelete()} className="text-red-600 hover:rotate-12 duration-200 transition-transform" />}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="border-y p-5">
                <form action="" onSubmit={addTable}>
                  <div className="flex items-center flex-wrap gap-y-2 justify-between">
                    <div className="flex gap-3 items-center">
                      <label>{t("manageTables.tableNumber")} :</label>
                      <input required value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} type="text" className="border-2 w-14 px-2 py-1 rounded-md" />
                    </div>
                    <div className="flex gap-3 items-center">
                      <label>{t("manageTables.numberOfChairs")} :</label>
                      <input required value={chairsCount} onChange={(e) => setchairsCount(e.target.value)} type="number" className="border-2 w-14 px-2 py-1 rounded-md" />
                    </div>
                    <div className="flex gap-3 items-center">
                      <label>{t("manageTables.qrNumber")} :</label>
                      <div className="border-2 px-2 py-1 rounded-md">
                        <select value={selectedQR} onChange={(e) => setSelectedQR(e.target.value)}>
                          <option value="">{t("manageTables.selectQr")}</option>
                          {QR?.map((qr) => (
                            <option key={qr._id} value={qr._id}>
                              {qr.serial}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <button className="my-btn mt-10">  {t("manageTables.addTable")}</button>
                </form>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={addFloor}
                  className="w-fit rounded-md bg-sidebar px-4 py-2 text-sm font-semibold text-skyblue disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 md:text-base"
                >
                  {t("manageTables.addNewFloor")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}